import DistributorLayout from '@/layouts/DistributorLayout.vue'
import RentalFileService from '@/services/rental-files/RentalFileService'
import { handleResponseError } from '@/utils/toastUtils'

// Temporary rollout compatibility. See legacy-url-transition.md before removing.
const redirectWithParams = (path, name) => ({
  path,
  redirect: (to) => ({ name, params: to.params, query: to.query, hash: to.hash })
})

const redirectWithDraft = (path, name, param, queryKey) => ({
  path,
  redirect: (to) => ({
    name,
    params: {},
    query: {
      ...to.query,
      ...(to.params[param] ? { [queryKey]: to.params[param] } : {})
    },
    hash: to.hash
  })
})

export default function createLegacyRoutes(router) {
  // Vue Router can apply a guard redirect after a newer navigation has started.
  let navigationVersion = 0
  router.beforeEach(() => {
    navigationVersion += 1
  })

  async function redirectLegacyRentalFile(to) {
    const requestVersion = navigationVersion
    try {
      let summary
      let lookupById = true
      // The old numeric URL overlaps the canonical reference URL. References can
      // contain only digits, so prefer an accessible reference before an ID lookup.
      if (to.path.startsWith('/rental-files/')) {
        try {
          summary = await RentalFileService.getRentalFileSummaryByBoomerangId(
            to.params.rentalFileId
          )
          lookupById = false
        } catch (error) {
          if (requestVersion !== navigationVersion) return false
          if (error.response?.data?.key !== 'rental_file_not_found_error') throw error
        }
      }
      if (lookupById) {
        summary = await RentalFileService.getRentalFileSummary(to.params.rentalFileId)
      }
      if (requestVersion !== navigationVersion) return false
      if (
        typeof summary?.rental_file_boomerang_id !== 'string' ||
        !summary.rental_file_boomerang_id
      ) {
        throw new Error('Missing rental file reference')
      }
      return {
        name: 'rentalFileDetail',
        params: { rentalFileBoomerangId: summary.rental_file_boomerang_id },
        query: to.query,
        hash: to.hash
      }
    } catch (error) {
      if (requestVersion !== navigationVersion) return false
      handleResponseError(error)
      return { name: 'dashboard' }
    }
  }

  return [
    redirectWithParams('/profile/collaborator/new', 'profileCollaboratorCreate'),
    redirectWithParams('/profile/collaborator/:collaboratorId', 'profileCollaborator'),
    redirectWithParams('/users/:accountId/collaborators/new', 'userCollaboratorCreate'),
    redirectWithParams('/rental-file/detail/:rentalFileBoomerangId', 'rentalFileDetail'),
    redirectWithParams('/repair-file/detail/:repairFileBoomerangId', 'repairFileDetail'),
    redirectWithDraft('/recycling/new/:id?', 'recyclingCreate', 'id', 'recyclingFolderBoomerangId'),
    redirectWithDraft(
      '/repair-file/:repairFileBoomerangId?',
      'repairFileCreate',
      'repairFileBoomerangId',
      'repairFileBoomerangId'
    ),
    {
      path: '/repair-file/finalization/:repairFileBoomerangId?',
      redirect: (to) => ({
        name: to.params.repairFileBoomerangId ? 'repairFileFinalization' : 'repairFileCreate',
        params: to.params.repairFileBoomerangId ? to.params : {},
        query: to.query,
        hash: to.hash
      })
    },
    redirectWithParams('/repair-file/shipping/:repairFileBoomerangId', 'repairFileShipping'),
    redirectWithParams(
      '/repair-file/customer-service-transport/:repairFileBoomerangId',
      'repairFileShipping'
    ),
    redirectWithParams('/rental-module/product-list/:repairFileBoomerangId', 'rentalProductList'),
    redirectWithParams(
      '/rental-module/product-detail/:repairFileBoomerangId/:rentalProductId',
      'rentalProductDetail'
    ),
    redirectWithParams(
      '/rental-module/rental-file-create/:repairFileBoomerangId/:rentalProductId',
      'rentalFileCreate'
    ),
    redirectWithParams('/rental-files/create/:repairFileBoomerangId', 'rentalProductList'),
    redirectWithParams(
      '/rental-files/create/:repairFileBoomerangId/products/:rentalProductId/contract',
      'rentalFileCreate'
    ),
    ...[
      '/rental-files/:rentalFileId(\\d+)',
      '/rental-module/rental-file-detail/:rentalFileId(\\d+)'
    ].map((path, index) => ({
      path,
      name: `legacyRentalFileDetail${index}`,
      // beforeEnter runs after the global authentication and permission guards.
      beforeEnter: redirectLegacyRentalFile,
      meta: {
        requiresAuth: true,
        layout: DistributorLayout,
        hideHeader: true,
        permissions: ['DISTRIBUTOR_RENTAL_READ'],
        menuSelected: 'repairFiles'
      }
    }))
  ]
}
